/* eslint-disable react/require-default-props */
import { Button } from '@pmi/dsm-react';
import { useSelector } from 'react-redux';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { DownloadIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { useLocation, useParams } from 'react-router';
import { ReportModel } from '../../shared/models/ReportModel';
import OverviewMobileView from './OverviewMobileView';
import CertificationTable from './ProductTypeTable/CertificationTable';
import CourseTable from './ProductTypeTable/CourseTable';
import DigitalProductTable from './ProductTypeTable/DigitalProductTable';
import MembershipTable from './ProductTypeTable/MembershipTable';
import styles from './report.module.scss';
import DateRangePicker from '../../shared/components/DateRangePicker';
import './customDateRange.scss';
import AuthService, { IAuthService } from '../../shared/services/AuthService';
import { accountSelector } from '../../shared/store/shared/accountSlice';
import { getOidcApiRoot } from '../../shared/env-vars';
import ReportTypeTab from './ReportTypeTab';
import { PMIFormatDate, saveAsResponse } from '../../shared/helpers';
import formatCurrency from '../../shared/utils/formatCurrency';
import overviewLabel from './overviewLabel';
import { getReportTotals } from './services';
import { ProductTypes } from '../../shared/constants';

let authService: IAuthService;

interface CurrentDealProps {
  reportDeals: ReportModel;
  reportType?: string;
  setReportType?: Dispatch<SetStateAction<string>>;
  startDate?: Date;
  setStartDate?: Dispatch<SetStateAction<Date>>;
  endDate?: Date;
  setEndDate?: Dispatch<SetStateAction<Date>>;
  startDateParam?: string;
  endDateParam?: string;
  isPayByUser?: boolean;
}

export default function ReportsOverview({
  reportDeals,
  reportType,
  setReportType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startDateParam,
  endDateParam,
  isPayByUser,
}: CurrentDealProps) {
  const { overview, header } = reportDeals;
  const total = useMemo(() => getReportTotals(overview), [overview]);
  const courses = overview?.courses;
  const certifications = overview?.certifications;
  const memberships = overview?.memberships;
  const digitalProducts = overview?.digitalProducts;
  const { currentAccountId } = useSelector(accountSelector);
  const [isDownloading, setIsDownloading] = useState(false);
  const { dealId } = useParams();
  const location = useLocation();

  const handleChange = (range: any) => {
    if (!range.startDate || !range.endDate) {
      return;
    }

    const dateStarted = new Date(range.startDate);
    const dateEnded = new Date(range.endDate);

    setStartDate(dateStarted);
    setEndDate(dateEnded);

    if (dateStarted && dateEnded) {
      window.history.replaceState(
        null,
        '',
        `${location.pathname.split(':')[0]}: ${PMIFormatDate(
          dateStarted.toDateString(),
        )} - ${PMIFormatDate(dateEnded.toDateString())}`,
      );
      setReportType(
        `Custom Date Range: ${PMIFormatDate(
          dateStarted.toDateString(),
        )} - ${PMIFormatDate(dateEnded.toDateString())}`,
      );
    }
  };

  const downloadFile = async () => {
    if (!authService) {
      authService = new AuthService();
    }
    try {
      setIsDownloading(true);
      const response = await fetch(
        `${getOidcApiRoot()}/api/accounts/${currentAccountId}/deals/${dealId}/reports/csv?StartDate=${encodeURIComponent(
          startDateParam,
        )}&EndDate=${encodeURIComponent(endDateParam)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'text/csv',
            Authorization: `Bearer ${
              (
                await authService.getUser()
              ).access_token
            }`,
            'X-SelectedAccountId': currentAccountId,
          },
        },
      );

      if (!response.ok) {
        return;
      }
      saveAsResponse(response, null);
      setIsDownloading(false);
    } catch (error) {
      console.error('Failed to download file', error);
      setIsDownloading(false);
    }
  };

  const overviewTypeDescription = (type: string) => {
    let description = '';

    switch (type) {
      case 'certification':
        description = `Developed by practitioners for practitioners, our certifications are based on rigorous standards and ongoing research to meet the real-world needs of organizations.`;
        break;
      case ProductTypes.membership:
        description = `As a PMI member, you can access PMI's publications available on the PMI store, on PMI's exams, and on PMI's online self learning courses covering project management.`;
        break;
      case 'digitalProducts':
        description = `Includes digital offerings and study tools not classified as courses but still relevant to your skill development.`;
        break;
      case 'courses':
        description = `Develop and enhance your power skills, increase your value to your organization and enhance your career opportunities through our self-paced learning, virtual or in-person training opportunities and more.`;
        break;
      default:
        break;
    }
    return description;
  };

  return (
    <div className="box mt-3">
      <div className="d-flex justify-content-between">
        <div>
          <h5 className="fw-normal fs-4">
            <div className="d-inline-block d-md-flex">
              <div className={`pe-2 ${styles.sectionHeader}`}>
                Overview{overviewLabel({ reportType, startDate, endDate })}
              </div>
            </div>
          </h5>
        </div>
        <div>
          <Button
            className="report_overview-download_csv-btn"
            size="md"
            disabled={isDownloading}
            icon={isDownloading ? DownloadIcon : null}
            iconAlign="right"
            titleText={`Download${isDownloading ? 'ing' : ''} .csv`}
            variant="primary"
            onClick={downloadFile}
          />
        </div>
      </div>
      <div className="report_overview-date_tabs">
        <ReportTypeTab
          reportType={reportType}
          onTabChange={setReportType}
          startDate={
            startDate
              ? PMIFormatDate(new Date(startDate).toDateString())
              : undefined
          }
          endDate={
            endDate
              ? PMIFormatDate(new Date(endDate).toDateString())
              : undefined
          }
        />
        {reportType.includes('Custom Date Range') && (
          <div className="customDateRange mt-3">
            <DateRangePicker
              className={styles.customDateRange}
              startDate={startDate}
              endDate={endDate}
              onChange={handleChange}
              disabledFuture
            />
          </div>
        )}
      </div>
      <div className="d-block d-md-flex mt-4">
        <div className={`${styles.cardContainer} ${styles.dataCard}`}>
          <div className="p-3">
            <h5 className={`fs-6 mb-0 ${styles.dataCardLabel}`}>
              Total Registered Users
            </h5>
            <span className={`fs-3 ${styles.dataCardValue}`}>
              {header?.registeredUsers}
            </span>
          </div>
        </div>
        <div className={`${styles.cardContainer} ${styles.dataCard}`}>
          <div className="p-3">
            <h5 className={`fs-6 mb-0 ${styles.dataCardLabel}`}>Total Spent</h5>
            <span className={`fs-3 ${styles.dataCardValue}`}>
              {`$${formatCurrency(total?.totalSpend ?? 0)}`}
            </span>
          </div>
        </div>
        <div className={`${styles.cardContainer} ${styles.dataCard}`}>
          <div className="p-3">
            <h5 className={`fs-6 mb-0 ${styles.dataCardLabel}`}>
              Total Products
            </h5>
            <span className={`fs-3 ${styles.dataCardValue}`}>
              {header?.products}
            </span>
          </div>
        </div>
      </div>

      {certifications?.length > 0 && (
        <>
          {/** DESKTOP */}
          <div
            className={`mt-4 pt-3 ${styles.cardContainer} d-none d-md-block`}
            data-testid="certification-table"
          >
            <div className="fw-bold fs-5 px-3">
              Certifications ({certifications.length})
            </div>
            <div className={`px-3 ${styles.productDescription}`}>
              {overviewTypeDescription('certification')}
            </div>
            <div className="pt-3">
              <CertificationTable
                reportDeals={reportDeals}
                reportType={reportType}
                total={total.certifications}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>

          {/** MOBILE */}
          <div
            className="mt-4 pt-3 d-block d-md-none"
            data-testid="certification-table"
          >
            <div className="fw-bold fs-5">
              Certifications ({certifications.length})
            </div>
            <div className={styles.productDescription}>
              {overviewTypeDescription('certification')}
            </div>
            <div className="pt-3">
              <OverviewMobileView
                data={certifications}
                dealId={dealId}
                reportType={reportType}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
        </>
      )}

      {memberships?.length > 0 && (
        <>
          {/** DESKTOP */}
          <div
            className={`mt-4 pt-3 ${styles.cardContainer} d-none d-md-block`}
            data-testid="membership-table"
          >
            <div className="fw-bold fs-5 px-3">
              Membership ({memberships.length})
            </div>
            <div className={`px-3 ${styles.productDescription}`}>
              {overviewTypeDescription(ProductTypes.membership)}
            </div>
            <div className="pt-3">
              <MembershipTable
                reportDeals={reportDeals}
                reportType={reportType}
                total={total.memberships}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>

          {/** MOBILE */}
          <div
            className="mt-4 pt-3 d-block d-md-none"
            data-testid="membership-table"
          >
            <div className="fw-bold fs-5">
              Membership ({memberships.length})
            </div>
            <div className={styles.productDescription}>
              {overviewTypeDescription(ProductTypes.membership)}
            </div>
            <div className="pt-3">
              <OverviewMobileView
                data={memberships}
                dealId={dealId}
                reportType={reportType}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
        </>
      )}

      {courses?.length > 0 && (
        <>
          {/** DESKTOP */}
          <div
            className={`mt-4 pt-3 ${styles.cardContainer}  d-none d-md-block`}
            data-testid="courses-table"
          >
            <div className="fw-bold fs-5 px-3">
              Online Courses ({courses.length})
            </div>
            <div className={`px-3 ${styles.productDescription}`}>
              {overviewTypeDescription('courses')}
            </div>
            <div className="pt-3">
              <CourseTable
                reportDeals={reportDeals}
                reportType={reportType}
                total={total.courses}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
          {/** MOBILE */}
          <div
            className="mt-4 pt-3 d-block d-md-none"
            data-testid="courses-table"
          >
            <div className="fw-bold fs-5">
              Online Courses ({courses.length})
            </div>
            <div className={styles.productDescription}>
              {overviewTypeDescription('courses')}
            </div>
            <div className="pt-3">
              <OverviewMobileView
                data={courses}
                dealId={dealId}
                reportType={reportType}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
        </>
      )}

      {digitalProducts?.length > 0 && (
        <>
          <div
            className={`mt-4 pt-3 ${styles.cardContainer}  d-none d-md-block`}
            data-testid="digitalProducts-table"
          >
            <div className="fw-bold fs-5 px-3">
              Digital Products ({digitalProducts.length})
            </div>
            <div className={`px-3 ${styles.productDescription}`}>
              {overviewTypeDescription('digitalProducts')}
            </div>
            <div className="pt-3">
              <DigitalProductTable
                reportDeals={reportDeals}
                reportType={reportType}
                total={total.digitalProducts}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
          {/** MOBILE */}
          <div
            className="mt-4 pt-3 d-block d-md-none"
            data-testid="courses-table"
          >
            <div className="fw-bold fs-5">
              Digital Products ({digitalProducts.length})
            </div>
            <div className={styles.productDescription}>
              {overviewTypeDescription('digitalProducts')}
            </div>
            <div className="pt-3">
              <OverviewMobileView
                data={digitalProducts}
                dealId={dealId}
                reportType={reportType}
                isPayByUser={isPayByUser}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
